// @ts-strict-ignore
import { Component } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AbstractModal } from "src/app/shared/components/modal/abstractModal";
import { ChannelAddress, Currency, CurrentData, EdgeConfig } from "src/app/shared/shared";
import { Controller_Ess_TimeOfUseTariffUtils } from "../utils";

@Component({
    templateUrl: "./modal.html",
    standalone: false,
})
export class ModalComponent extends AbstractModal {

    protected readonly CONVERT_TIME_OF_USE_TARIFF_STATE = this.Utils.CONVERT_TIME_OF_USE_TARIFF_STATE(this.translate);
    protected priceWithCurrency: string;
    protected consumptionPriceWithCurrency: string;
    protected productionPriceWithCurrency: string;

    protected override getFormGroup(): FormGroup {
        const allowChargeFromGrid = this.component.properties.allowChargeFromGrid
            ?? (this.component.properties.controlMode === Controller_Ess_TimeOfUseTariffUtils.ControlMode.CHARGE_CONSUMPTION);
        const enableDischargeGrid = this.component.properties.enableDischargeGrid
            ?? (this.component.properties.controlMode === Controller_Ess_TimeOfUseTariffUtils.ControlMode.DISCHARGE_TO_GRID);

        return this.formBuilder.group({
            mode: new FormControl(this.component.properties.mode),
            controlMode: new FormControl(this.component.properties.controlMode),

            allowChargeFromGrid: new FormControl(allowChargeFromGrid),
            enablePeakShavingReserve: new FormControl(this.component.properties.enablePeakShavingReserve ?? true),
            peakShavingPower: new FormControl(this.component.properties.peakShavingPower ?? 5000, [
                Validators.required,
                Validators.min(0),
            ]),
            enableDischargeGrid: new FormControl(enableDischargeGrid),
            dischargeToGridPower: new FormControl(this.component.properties.dischargeToGridPower ?? 5000, [
                Validators.required,
                Validators.min(0),
            ]),
            minSocForDischargeGrid: new FormControl(this.component.properties.minSocForDischargeGrid ?? 20, [
                Validators.required,
                Validators.min(0),
                Validators.max(100),
            ]),

            // Backward-compatible helper for old templates/config migrations.
            chargeConsumptionIsActive: new FormControl(allowChargeFromGrid),
        });
    }

    protected override getChannelAddresses(): ChannelAddress[] {
        return [
            new ChannelAddress(this.component.id, "QuarterlyPrices"),
            new ChannelAddress(this.component.id, "QuarterlyConsumptionPrices"),
            new ChannelAddress(this.component.id, "QuarterlyProductionPrices"),
        ];
    }

    protected override onIsInitialized(): void {
        this.subscription.add(
            this.formGroup?.get("chargeConsumptionIsActive")
                ?.valueChanges
                .subscribe(isActive => {
                    this.formGroup.controls["allowChargeFromGrid"].setValue(isActive, { emitEvent: false });
                    this.formGroup.controls["allowChargeFromGrid"].markAsDirty();
                    this.updateControlMode();
                }));

        this.subscription.add(
            this.formGroup?.get("allowChargeFromGrid")
                ?.valueChanges
                .subscribe(isActive => {
                    this.formGroup.controls["chargeConsumptionIsActive"].setValue(isActive, { emitEvent: false });
                    this.updateControlMode();
                }));

        this.subscription.add(
            this.formGroup?.get("enableDischargeGrid")
                ?.valueChanges
                .subscribe(() => this.updateControlMode()));
    }

    protected override onCurrentData(currentData: CurrentData): void {
        const quarterlyPrice = currentData.allComponents[this.component.id + "/QuarterlyPrices"];
        const quarterlyConsumptionPrice = currentData.allComponents[this.component.id + "/QuarterlyConsumptionPrices"];
        const quarterlyProductionPrice = currentData.allComponents[this.component.id + "/QuarterlyProductionPrices"];

        const meta: EdgeConfig.Component = this.config?.getComponent("_meta");
        const currency: string = this.config?.getPropertyFromComponent<string>(meta, "currency");
        const currencyLabel: Currency.Label = Currency.getCurrencyLabelByCurrency(currency);

        this.priceWithCurrency = this.Utils.CONVERT_PRICE_TO_CENT_PER_KWH(2, currencyLabel)(quarterlyPrice);
        this.consumptionPriceWithCurrency = this.Utils.CONVERT_PRICE_TO_CENT_PER_KWH(2, currencyLabel)(quarterlyConsumptionPrice ?? quarterlyPrice);
        this.productionPriceWithCurrency = this.Utils.CONVERT_PRICE_TO_CENT_PER_KWH(2, currencyLabel)(quarterlyProductionPrice ?? quarterlyPrice);
    }

    private updateControlMode(): void {
        const controlMode = Controller_Ess_TimeOfUseTariffUtils.getControlMode(
            this.formGroup.controls["allowChargeFromGrid"].value === true,
            this.formGroup.controls["enableDischargeGrid"].value === true,
        );
        this.formGroup.controls["controlMode"].setValue(controlMode);
        this.formGroup.controls["controlMode"].markAsDirty();
    }
}
